import React, { useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { generateDemoRequestXml, generateXmlFilename } from '../services/xmlGenerator';
import { uploadXmlToBlobStorage, validateDomainId, getSasToken, simulateXmlUpload } from '../services/blobStorageService';
import { processDemoRequest } from '../services/zohoCrmService';
import { verifyCaptchaToken, isValidCaptchaToken } from '../services/captchaService';
import Captcha from '../components/Captcha';
import RECAPTCHA_CONFIG from '../config/captchaConfig';
import { usePageTitle } from '../hooks/usePageTitle';

const RequestDemo = () => {
  const { brand } = useTheme();
  const { styles } = useVariant();
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('request-demo');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    fleetSize: '',
    notes: '',
    createdOn: new Date().toISOString()
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef(null);

  // Domain-specific demo request content
  const getDemoRequestContent = () => {
    const domainId = brand?.id;
    
    switch (domainId) {
      case 'geotab-camera':
        return {
          title: 'Request a Camera Demo',
          subtitle: 'See our professional fleet camera solutions in action',
          description: 'Experience the power of our advanced camera systems with a personalized demo tailored to your fleet needs.',
          placeholder: 'Any specific camera features you\'re interested in or questions you have...'
        };
      case 'geotab-dashcam':
      case 'geotab-dashcams':
        return {
          title: 'Request a Dash Cam Demo',
          subtitle: 'Professional dash camera demonstration',
          description: 'Watch our premium dash cameras in action and see how they can enhance your fleet safety.',
          placeholder: 'Any specific dash cam features you\'re interested in or questions you have...'
        };
      case 'road-eazy':
        return {
          title: 'Request a RoadEazy Demo',
          subtitle: 'Advanced road monitoring demonstration',
          description: 'Discover how our AI-powered monitoring system can transform your fleet operations.',
          placeholder: 'Any specific monitoring features you\'re interested in or questions you have...'
        };
      case 'smarter-tracking':
        return {
          title: 'Request a Smart Tracking Demo',
          subtitle: 'Intelligent fleet management demonstration',
          description: 'See how our AI-powered tracking solutions can optimize your fleet operations.',
          placeholder: 'Any specific tracking features you\'re interested in or questions you have...'
        };
      case 'fleet-dashcam':
        return {
          title: 'Request a Fleet Dash Cam Demo',
          subtitle: 'Professional dash camera buying guide',
          description: 'Explore how Fleet Dash Cam can help you run your business and fleet more efficiently, while ensuring your drivers are performing safely.',
          placeholder: 'Any specific dash cam features you\'re interested in or questions you have...'
        };
      default:
        return {
          title: 'Request a Demo',
          subtitle: 'Experience our solutions',
          description: 'See our products and services in action with a personalized demonstration.',
          placeholder: 'Any specific features you\'re interested in or questions you have...'
        };
    }
  };

  const { title, subtitle, description, placeholder } = getDemoRequestContent();

  // Captcha handlers
  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken('');
    setCaptchaError('Captcha expired. Please verify again.');
  };

  const handleCaptchaError = (error) => {
    setCaptchaToken('');
    setCaptchaError('Captcha verification failed. Please try again.');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email address is invalid';
      isValid = false;
    }

    // Phone validation - only if provided
    if (formData.phone.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      const cleanPhone = formData.phone.replace(/[\s\-\(\)\.]/g, '');
      if (!phoneRegex.test(cleanPhone) || cleanPhone.length < 10) {
        errors.phone = 'Please enter a valid phone number';
        isValid = false;
      }
    }

    // Captcha validation
    if (!captchaToken) {
      setCaptchaError('Please complete the captcha verification');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Verify captcha token
        if (!isValidCaptchaToken(captchaToken)) {
          throw new Error('Invalid captcha token');
        }

        const captchaVerification = await verifyCaptchaToken(captchaToken);
        if (!captchaVerification.success) {
          throw new Error(captchaVerification.error || 'Captcha verification failed');
        }

        const domainId = brand?.id || 'default';
        const fullDomain = window.location.hostname || 'localhost';
        
        
        // Validate domain ID
        if (!validateDomainId(domainId)) {
          throw new Error('Invalid domain ID');
        }
        
        // Update createdOn timestamp
        const submitDateTime = new Date().toISOString();
        const updatedFormData = {
          ...formData,
          createdOn: submitDateTime,
          captchaToken: captchaToken
        };
        
        // Generate XML content with full domain
        const xmlContent = generateDemoRequestXml(updatedFormData, fullDomain);
        
        // Generate filename with full name and submit datetime
        const filename = generateXmlFilename(domainId, updatedFormData.name, submitDateTime);
        
        // Try to get SAS token for authentication
        const sasToken = await getSasToken();
        
        // Upload to blob storage with SAS token if available
        let uploadResult;
        if (sasToken) {
          uploadResult = await uploadXmlToBlobStorage(xmlContent, filename, domainId, sasToken);
        } else {
          // Fallback to simulated upload for testing when no authentication is available
          uploadResult = await simulateXmlUpload(xmlContent, filename, domainId);
        }
        
        if (uploadResult.success) {
          // Process demo request
          const zohoResult = await processDemoRequest(updatedFormData);
          
          if (zohoResult.success) {
            // Demo request processed successfully
            // Our team has been notified
          } else {
            // Demo request processing failed
            // Continue with form submission even if Zoho fails
          }
          
          // Reset form after successful submission
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            fleetSize: '',
            notes: '',
            createdOn: new Date().toISOString()
          });
          
          // Reset captcha
          setCaptchaToken('');
          setCaptchaError('');
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
          
          setSubmitError('');
          setIsSubmitted(true);
        } else {
          throw new Error(`Upload failed: ${uploadResult.error}`);
        }
      } catch (error) {
        setSubmitError(`Error submitting form: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className={`min-h-screen pt-24 pb-12 ${styles.container}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl shadow-lg p-8 md:p-10 ${styles.card}`}>
            <div className="text-center">
              <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${styles.accent} bg-opacity-10`}>
                <svg className={`h-8 w-8 ${styles.accent}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className={`mt-4 text-3xl font-extrabold ${styles.text}`}>Thank you!</h2>
              <p className={`mt-3 text-lg ${styles.textSecondary}`}>
                Your demo request has been submitted successfully. Our team will contact you shortly.
              </p>
              <div className="mt-8">
                <button 
                  onClick={() => setIsSubmitted(false)} 
                  className={`px-6 py-3 rounded-lg transition-all duration-300 ${styles.button}`}
                >
                  Submit another request
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-24 pb-12 ${styles.container}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className={`text-3xl font-extrabold sm:text-4xl ${styles.text}`}>
            {h1Title || title}
          </h1>
          <p className={`mt-4 text-xl max-w-2xl mx-auto ${styles.textSecondary}`}>
            {subtitle}
          </p>
          <p className={`mt-2 text-lg max-w-2xl mx-auto ${styles.textSecondary}`}>
            {description}
          </p>
        </div>
        
        <div className={`rounded-2xl shadow-lg overflow-hidden ${styles.card}`}>
          <div className="p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className={`block text-sm font-medium ${styles.text}`}>
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`block w-full rounded-md px-4 py-3 border ${
                      formErrors.name ? 'border-red-300 ring-red-500' : ''
                    } shadow-sm sm:text-sm ${styles.input}`}
                    placeholder="John Doe"
                  />
                  {formErrors.name && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className={`block text-sm font-medium ${styles.text}`}>
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-md px-4 py-3 border ${
                      formErrors.email ? 'border-red-300 ring-red-500' : ''
                    } shadow-sm sm:text-sm ${styles.input}`}
                    placeholder="john@example.com"
                  />
                  {formErrors.email && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
              </div>

              {/* Company Field */}
              <div>
                <label htmlFor="company" className={`block text-sm font-medium ${styles.text}`}>
                  Company Name <span className={`text-xs ${styles.textSecondary}`}>(optional)</span>
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={`block w-full rounded-md px-4 py-3 border shadow-sm sm:text-sm ${styles.input}`}
                    placeholder="Your Company Inc."
                  />
                </div>
              </div>

              {/* Fleet Size Field */}
              <div>
                <label className={`block text-sm font-medium ${styles.text} mb-3`}>
                  Fleet Size <span className={`text-xs ${styles.textSecondary}`}>(optional)</span>
                </label>
                <div className="space-y-3">
                  {[
                    { value: '1-9', label: 'Under 10 vehicles' },
                    { value: '11-100', label: '11 - 100 vehicles' },
                    { value: '101-1000', label: '101 - 1000 vehicles' },
                    { value: '1000+', label: '1000+ vehicles' }
                  ].map((option) => (
                    <div key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="fleetSize"
                        id={`fleetSize-${option.value}`}
                        value={option.value}
                        checked={formData.fleetSize === option.value}
                        onChange={handleChange}
                        className={`h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 ${styles.input}`}
                      />
                      <label htmlFor={`fleetSize-${option.value}`} className={`ml-3 block text-sm ${styles.text}`}>
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className={`block text-sm font-medium ${styles.text}`}>
                  Phone Number <span className={`text-xs ${styles.textSecondary}`}>(optional)</span>
                </label>
                <div className="mt-1">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`block w-full rounded-md px-4 py-3 border ${
                      formErrors.phone ? 'border-red-300 ring-red-500' : ''
                    } shadow-sm sm:text-sm ${styles.input}`}
                    placeholder="(555) 123-4567"
                  />
                  {formErrors.phone && (
                    <p className="mt-2 text-sm text-red-600">{formErrors.phone}</p>
                  )}
                </div>
              </div>

              {/* Notes Field */}
              <div>
                <label htmlFor="notes" className={`block text-sm font-medium ${styles.text}`}>
                  Special Requests <span className={`text-xs ${styles.textSecondary}`}>(optional)</span>
                </label>
                <div className="mt-1">
                  <textarea
                    name="notes"
                    id="notes"
                    rows={4}
                    value={formData.notes}
                    onChange={handleChange}
                    className={`block w-full rounded-md px-4 py-3 border shadow-sm sm:text-sm ${styles.input}`}
                    placeholder={placeholder}
                  />
                </div>
              </div>

              {/* Captcha */}
              <div>
                <label className={`block text-sm font-medium ${styles.text} mb-3`}>
                  Security Verification <span className="text-red-500">*</span>
                </label>
                <Captcha
                  ref={captchaRef}
                  siteKey={RECAPTCHA_CONFIG.siteKey}
                  onVerify={handleCaptchaVerify}
                  onExpire={handleCaptchaExpire}
                  onError={handleCaptchaError}
                  theme={RECAPTCHA_CONFIG.theme}
                  size={RECAPTCHA_CONFIG.size}
                />
                {captchaError && (
                  <p className="mt-2 text-sm text-red-600">{captchaError}</p>
                )}
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{submitError}</p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setSubmitError('')}
                          className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full md:w-auto px-8 py-3 rounded-lg flex items-center justify-center transition-all duration-200 ${styles.button} ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Request Demo'
                  )}
                </button>
              </div>
            </form>
          </div>
          
          <div className={`px-8 md:px-10 py-6 border-t border-white/10 ${styles.section}`}>
            <p className={`text-center text-sm ${styles.textSecondary}`}>
              By submitting this form, you agree to our{' '}
              <a href="/privacy" className={`font-medium ${styles.accent} hover:opacity-80`}>
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDemo; 